import React, { useCallback, useEffect, useRef, useState } from 'react';
import classes from './AccessInsights.module.scss';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { BannerDescription } from 'new/elements/BannerDescription';
import { Stack } from 'new/elements/Stack';
import { Typography } from '@mui/material';
import { ROUTE_ACCESS_INSIGHTS } from 'new/constants/pathnames';
import { ResourceSearch } from 'new/elements/ResourceSearch';
import { MembersInput } from 'new/components/collaborate/AccessInsights/MembersInput';
import { Button } from 'new/elements/Button';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { fetchAccessInsights, fetchOrgMembers } from 'js/actions';
import { useDispatch } from 'react-redux';
import { Resume } from 'new/components/monitor/ResourceTriage/components/Resume';
import { useResource } from 'new/components/monitor/ResourceTriage/useResource';
import { ACLQueryModeTypes } from 'js/constants/visibilityFilters';
import { resourceFetchActions } from 'new/components/monitor/ResourceTriage/ResourceTriage';
import { ResourceTypeData } from 'new/components/monitor/ResourceTriage/components/ResourceType';
import { ArrowForward } from '@mui/icons-material';
import cx from 'classnames';
import { useOrgMembers } from 'new/hooks/adapters/useOrgMembers';
import { Link, useHistory } from 'react-router-dom';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_ACCESS_INSIGHTS_REQUEST, GET_ORG_MEMBERS_REQUEST } from 'js/constants/actionTypes';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { ModalFlowPreview } from 'new/elements/ModalFlowPreview';
import { useModals } from 'new/hooks/useModals';
import NoContentTextView from 'js/components/elements/NoContentTextView';
import useAccessRules from 'new/hooks/adapters/useAccessRules';
import { EmptyState } from 'new/elements/EmptyState/EmptyState';

interface AccessInsightsProps {}

const description = 'Gain a better understanding about why a person has access to a Resource.';

const lookupRoleText = {
  admin: 'full administrative',
  collaborator: 'collaborator',
  operator: 'operator',
};

export const AccessInsights = (props: AccessInsightsProps) => {
  const history = useHistory();
  const initialParams = new URLSearchParams(history.location.search);
  const memberId = initialParams.get('member');
  const type = initialParams.get('type');
  const id = initialParams.get('id');

  const members = useOrgMembers();

  const { closeModal } = useModals();

  const [member, setMember] = useState(
    memberId ? members.find((m) => m.id === Number(memberId)) : null,
  );
  const requested = useRef(false);
  const [fields, setFields] = useState({ type, id, member });
  const { resource } = useResource(fields.type, fields.id);
  const { applicableRules: rules, effectiveRule } = useAccessRules(
    fields.id,
    fields.type,
    fields.member?.id,
  );
  const applicableRules = rules.filter(
    (rule) =>
      rule.access_role !== effectiveRule.access_role ||
      rule.description !== effectiveRule.description,
  );
  const dispatch = useDispatch();
  const loadingMembers = useIsLoading(GET_ORG_MEMBERS_REQUEST);
  const loading = useIsLoading(GET_ACCESS_INSIGHTS_REQUEST);
  const org = useCurrentOrg();

  useEffect(() => {
    if (memberId) {
      const member = members.find((m) => m.id === Number(memberId));
      setMember(member);
      setFields((fields) => ({ ...fields, member }));
    }
  }, [members, memberId]);

  useEffect(() => {
    if (org.id) {
      dispatch(fetchOrgMembers(org.id));
    }
  }, [org.id]);

  const onChangeResource = useCallback(({ type, id }) => {
    setFields((fields) => ({ ...fields, type, id }));
  }, []);

  const onSelectMember = useCallback((member) => {
    setMember(member);
    setFields((fields) => ({ ...fields, member }));
  }, []);

  const updateUrl = (fields) => {
    const params = new URLSearchParams(history.location.search);
    Object.keys(fields).forEach((key) => {
      const value = fields[key];
      params.set(key, typeof value === 'object' ? value.id : value);
    });
    history.replace({
      pathname: history.location.pathname,
      search: params.toString(),
    });
  };

  const request = (fields) => {
    if (!Object.values(fields).some((a) => !a)) {
      updateUrl(fields);
      const action = resourceFetchActions[fields.type];
      if (action) {
        dispatch(action(fields.id, ACLQueryModeTypes.MODE_COLLABORATOR.key));
      }
      dispatch(
        fetchAccessInsights({
          resourceType: fields.type,
          id: fields.id,
          userId: fields.member?.id,
        }),
      );
    }
  };

  const onFind = (e) => {
    requested.current = true;
    e.preventDefault();
    request(fields);
  };

  const onItemClick = (type, data) => {
    closeModal(ModalFlowPreview.modalConfig.name);
    setFields((fields) => ({ ...fields, type, id: data.id }));
    request({ ...fields, type, id: data.id });
  };

  return (
    <div className={classes.container}>
      <BannerDescription title="Access Insights" description={description} />
      <form onSubmit={onFind} className={classes.formContainer}>
        <Stack width="70%" gap="16px">
          {loadingMembers ? null : <MembersInput initialValue={member} onSelect={onSelectMember} />}
          <Stack>
            <ResourceSearch
              type={fields.type}
              id={fields.id}
              fullWidth
              onChange={onChangeResource}
            />
          </Stack>
          <Button
            type="submit"
            className={classes.find}
            color="primary"
            variant="contained"
            disabled={Object.values(fields).some((a) => !a)}
          >
            Find
          </Button>
        </Stack>
        {loadingMembers ? (
          <div className={classes.loaderSearch}>
            <CenteredLoader minHeight={85} />
          </div>
        ) : null}
      </form>

      {loading ? <CenteredLoader height={100} /> : null}

      {!requested.current && !loading && !resource && (
        <EmptyState
          title="Find a Resource to Begin"
          description="Enter a person’s name or email and select a Resource to view access insights."
          tip={
            <>
              Need help finding user information? Check your organization’s directory or ask your
              admin.
            </>
          }
        />
      )}

      {effectiveRule && !effectiveRule.access_role && (
        <NoContentTextView
          title={(member.full_name || member.name) + ' has no access to this resource'}
        />
      )}

      {!!resource && !!effectiveRule?.access_role && (
        <Stack direction="row">
          <div className={classes.content}>
            <Stack direction="row">
              <div className={classes.fieldsInfo}>
                <Stack direction="row" alignItems="center" gap="8px">
                  <Typography variant="body1">{member.full_name}</Typography>
                  <div className={classes.role}>
                    <span>{member.access_role}</span>
                  </div>
                </Stack>
                <Typography variant="caption">{member.email}</Typography>
              </div>
              <Stack className={classes.fieldsInfo}>
                <Typography variant="body1">
                  {ResourceTypeData[fields.type]?.label} {fields.id}
                </Typography>
              </Stack>
            </Stack>
            <div className={classes.info}>
              <Typography variant="caption">
                The following access grant gives {member.full_name}{' '}
                {lookupRoleText[effectiveRule.access_role]} rights to this resource...
              </Typography>
            </div>
            <div className={cx(classes.ruleContainer, classes.ruleContainer_effective)}>
              <div className={classes.ruleDescription}>
                <Typography variant="body1">{effectiveRule.description}</Typography>
                <ArrowForward />
              </div>
              <div className={classes.ruleDescription}>
                <Stack>
                  <Typography variant="body1">{effectiveRule.access_role}</Typography>
                  <Typography variant="caption"></Typography>
                </Stack>
              </div>
            </div>
            {!!applicableRules?.length ? (
              <>
                <div className={classes.info}>
                  <Typography variant="caption">
                    Additionally {member.full_name} has access to {fields.id} through...
                  </Typography>
                  <Typography variant="caption">with the following access grant...</Typography>
                </div>
                {applicableRules?.map((rule, index) => (
                  <div key={index} className={cx(classes.ruleContainer)}>
                    <div className={classes.ruleDescription}>
                      <Typography variant="body1">{rule.description}</Typography>
                      <ArrowForward />
                    </div>
                    <div className={classes.ruleDescription}>
                      <Stack>
                        <Typography variant="body1">{rule.access_role}</Typography>
                        <Typography variant="caption"></Typography>
                      </Stack>
                    </div>
                  </div>
                ))}
              </>
            ) : null}
          </div>
          <Stack width="30%">
            <Resume
              onFlowItemClicked={onItemClick}
              {...resource}
              resourceType={fields.type}
              id={fields.id}
            />
          </Stack>
        </Stack>
      )}
    </div>
  );
};

AccessInsights.screenConfig = {
  path: `${ROUTE_REDESIGN}${ROUTE_ACCESS_INSIGHTS}`,
  domain: 'collaborate',
  modals: [ModalFlowPreview],
  header: {
    title: 'Access Insights',
    slugTitle: 'Access Insights',
  },
  screenName: 'AccessInsights',
};
